import { useState } from 'react';
import { X, Key, AlertTriangle, CheckCircle } from 'lucide-react';
import { doctorsApi, usersApi } from '../../lib/api';

interface ResetPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  doctorId: string | null;
  doctorName: string;
}

const ResetPasswordModal = ({ isOpen, onClose, doctorId, doctorName }: ResetPasswordModalProps) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  const handleResetPassword = async () => {
    if (!doctorId) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await usersApi.resetPassword(doctorId, {
        newPassword: 'tempPassword123',
        confirmPassword: 'tempPassword123'
      });
      
      if (response.data.success) {
        setTempPassword(response.data.data!.tempPassword);
        setEmail(response.data.data!.email);
        setSuccess(true);
      } else {
        setError(response.data.message || 'Failed to reset password');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setError(null);
    setTempPassword('');
    setEmail('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Key className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
              <p className="text-sm text-gray-500">for Dr. {doctorName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!success ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <h3 className="text-sm font-medium text-amber-800">Warning</h3>
                  <p className="text-sm text-amber-700">
                    This will reset the doctor's password and generate a new temporary password. 
                    The doctor will need to change it on their next login.
                  </p>
                </div>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                </div>
              )}

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  Are you sure you want to reset the password for <strong>Dr. {doctorName}</strong>?
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">Password Reset Successful</h3>
                  <p className="text-sm text-green-700">
                    The password has been reset successfully.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-sm text-gray-900">{email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Temporary Password
                  </label>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <span className="text-sm font-mono text-gray-900">{tempPassword}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Share this temporary password with the doctor. They must change it on their next login.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          {!success ? (
            <>
              <button
                onClick={handleClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="btn-primary bg-red-600 hover:bg-red-700"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </>
          ) : (
            <button
              onClick={handleClose}
              className="btn-primary"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal; 
